import { lazy, FC, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'

import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { WithChildren } from '../../_metronic/helpers'
import { useSelector } from 'react-redux'
import { RootState } from '../redux/store'
import ProtectedRoute from './ProtectedRoutes'
import lib from '../biz/lib'
import { getIsAllSubscrExpired } from '../redux/features/userSlice'


const PrivateRoutes = () => {
  const DashboardPage = lazy(() => import('../pages/DashboardPage'))
  const PropertyUnitPage = lazy(() => import('../pages/PropertyUnitPage'))
  const PropertyRoomPage = lazy(() => import('../pages/PropertyRoomPage'))
  const MaintenancePage = lazy(() => import('../pages/MaintenancePage'))
  const TenantDataPage = lazy(() => import('../pages/TenantDataPage'))
  const ActiveTenantPage = lazy(() => import('../pages/ActiveTenantPage'))
  const ETenanciesPage = lazy(() => import('../pages/ETenanciesPage'))
  const LandlordDataPage = lazy(() => import('../pages/LandLordDataPage'))
  const ManagementAgreementPage = lazy(() => import('../pages/ManagementAgreementPage'))
  const ActiveLanlordPage = lazy(() => import('../pages/ActiveLandlordPage'))
  const OperatorDataPage = lazy(() => import('../pages/OperatorDataPage'))
  const ActiveOperatorPage = lazy(() => import('../pages/ActiveOperatorPage'))
  const OwnerDataPage = lazy(() => import('../pages/OwnerDataPage'))
  const OwnerPaymentPage = lazy(() => import('../pages/OwnerPaymentPage'))
  const SubscriptionPage = lazy(() => import('../pages/owner-subscriptions/SubscriptionPage'))
  const SubscriptionBatchDetailsPage = lazy(() => import('../pages/owner-subscriptions/SubscriptionBatchDetailsPage'))
  const StorePage = lazy(() => import('../pages/StorePage'))
  const OfficeDataPage = lazy(() => import('../pages/OfficeDataPage'))
  const TenantDashboardPage = lazy(() => import('../pages/TenantDashboardPage'))
  const UserProfilePage = lazy(() => import('../pages/UserProfilePage'))
  const ChangePasswordPage = lazy(() => import('../pages/ChangePasswordPage'))
  const PartnerApplicationPage = lazy(() => import('../pages/PartnerApplicationPage'))
  const BillingPage = lazy(() => import('../pages/BillingPage'))
  const ProductPaymentPage = lazy(() => import('../pages/owner-subscriptions/ProductPaymentPage'));
  const PaymentVoucherPage = lazy(() => import('../pages/PaymentVoucherPage'))
  // const RecordPaymentPage = lazy(() => import('../pages/RecordPaymentPage')) // to be confirm
  const PaymentStatusPage = lazy(() => import('../pages/PaymentStatusPage'))
  const ReceiptPage = lazy(() => import('../pages/ReceiptPage'))
  const DepositRefundPage = lazy(() => import('../pages/DepositRefundPage'))
  const ExpensesPage = lazy(() => import('../pages/ExpensesPage'))
  const SmartMeterPage = lazy(() => import('../pages/SmartMeterPage'))
  const SmartLockPage = lazy(() => import('../pages/SmartLockPage'))
  const SmartLockDetailsPage = lazy(() => import('../pages/SmartLockDetailsPage'))
  const SmartLockGatewayPage = lazy(() => import('../pages/SmartLockGatewayPage'))
  // const PaymentPage = lazy(() => import('../pages/PaymentPage'))
  const InventoryListPage = lazy(() => import('../pages/InventoryListPage'))
  const InventoryTransactionPage = lazy(() => import('../pages/InventoryTransactionPage'))
  const PropertyPayoutReportPage = lazy(() => import('../pages/PropertyPayoutReportPage'))
  const SaleReportPage = lazy(() => import('../pages/SaleReportPage'))
  const UserRolePage = lazy(() => import('../pages/UserRolePage'))
  const AdministratorProfilePage = lazy(() => import('../pages/AdministratorProfilePage'))
  const AuditTrailsPage = lazy(() => import('../pages/AuditTrailsPage'))
  const PaymentTransactionPage = lazy(() => import('../pages/PaymentTransactionPage'))
  const OwnerSettingPage = lazy(() => import('../pages/OwnerSettingPage'))
  const BillTypePage = lazy(() => import('../pages/BillTypePage'))
  const ExpensesTypePage = lazy(() => import('../pages/ExpensesTypePage'))
  const ContractorDataPage = lazy(() => import('../pages/ContractorDataPage'))
  const SettingPage = lazy(() => import('../pages/SettingPage'))
  const AnnouncementPage = lazy(() => import('../pages/AnnouncementPage'))
  const NextsixListingPage = lazy(() => import('../pages/Next6ListingPage'))
  const ShowcaseListingPage = lazy(() => import('../pages/ShowcaseListingPage'))
  const PurchaseHistoryPage = lazy(() => import('../pages/PurchaseHistoryPage'))
  const EInvoiceSetUpPage = lazy(() => import('../pages/EInvoiceSetUpPage'))
  const CreditNotePage = lazy(() => import('../pages/CreditNotePage'))
  const DebitNotePage = lazy(() => import('../pages/DebitNotePage'))
  const PayoutTransactionPage = lazy(() => import('../pages/PayoutTransactionPage'))
  const StoreCheckoutPage = lazy(() => import('../pages/StoreCheckoutPage'))
  const PropertySitePage = lazy(() => import('../pages/PropertySitePage'))

  const userSlice = useSelector((state: RootState) => state.user)
  const userModule = userSlice?.profile?.modules

  const isSubscriptionExpired = useSelector(getIsAllSubscrExpired);


  return (
    <Routes>
      <Route element={<MasterLayout />}>

        {userSlice.role !== 'Tenant' ?
          <Route path='*' element={<Navigate to={'/dashboard'} />} /> :
          <Route path='*' element={<Navigate to={'/tenant-dashboard'} />} />
        }

        <Route
          path='dashboard'
          element={
            <SuspensedView>
              <div style={{ pointerEvents: isSubscriptionExpired ? 'none' : 'auto', opacity: isSubscriptionExpired ? 0.4 : 1 }}>
                <DashboardPage />
              </div>
            </SuspensedView>
          }
        />

        <Route
          path='tenant-dashboard'
          element={
            <SuspensedView>
              <TenantDashboardPage />
            </SuspensedView>
          }
        />

        <Route
          path='profile'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={UserProfilePage}
                moduleName="User Profile"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='change-password'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ChangePasswordPage}
                moduleName="Change Password"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='partner-application'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PartnerApplicationPage}
                moduleName="Partner Application"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='setting'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={OwnerSettingPage}
                moduleName="Setting"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='administration/user-role'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={UserRolePage}
                moduleName="User Role"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='administration/profile'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={AdministratorProfilePage}
                moduleName="Administrator Profile"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='administration/audit-trails'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={AuditTrailsPage}
                moduleName="Audit Trails"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='property/unit'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PropertyUnitPage}
                moduleName="Unit"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='property/room'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PropertyRoomPage}
                moduleName="Room"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='property/maintenance'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={MaintenancePage}
                moduleName="Maintenance"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='tenant/data'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={TenantDataPage}
                moduleName="Tenant Data"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='tenant/active'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ActiveTenantPage}
                moduleName="Active Tenant"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='tenant/e-tenancies'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ETenanciesPage}
                moduleName="E-Tenancies"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='operator/data'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={OperatorDataPage}
                moduleName="Operator Data"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='operator/active'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ActiveOperatorPage}
                moduleName="Active Operator"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='landlord/data'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={LandlordDataPage}
                moduleName="Landlord Data"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='landlord/active'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ActiveLanlordPage}
                moduleName="Active Landlord"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='landlord/agreement'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ManagementAgreementPage}
                moduleName="Management Agreement"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='owner/data'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={OwnerDataPage}
                moduleName="Owner Data"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        {/* <Route
          path='owner/payment' // access module was archived
          element={
            <SuspensedView>
              <ProtectedRoute
                component={OwnerPaymentPage}
                moduleName="Owner Payment"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        /> */}
        <Route
          path='owner/subscription'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SubscriptionPage}
                moduleName="Owner Subscription"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='owner/my-subscription'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SubscriptionPage}
                moduleName="My Subscription"
                userModules={userModule}
                skipDimOverlay={true}
              />
            </SuspensedView>
          }
        />
        <Route
          path='owner/my-invoice'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ProductPaymentPage}
                moduleName="My Owner Bill"
                userModules={userModule}
                skipDimOverlay={true}
              />
            </SuspensedView>
          }
        />
        <Route
          path='owner/subscription/batch/details'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SubscriptionBatchDetailsPage}
                moduleName="Subscription Batch Details"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='service-cart/store'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={StorePage}
                moduleName="Store"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route path="service-cart/checkout">
          <Route
            path=':transactionId'
            element={
              <SuspensedView>
                <ProtectedRoute
                  component={StoreCheckoutPage}
                  moduleName="Store"
                  userModules={userModule}
                  skipDimOverlay={false}
                />
              </SuspensedView>
            }
          />
          <Route
            path=''
            element={
              <SuspensedView>
                <ProtectedRoute
                  component={StoreCheckoutPage}
                  moduleName="Store"
                  userModules={userModule}
                  skipDimOverlay={false}
                />
              </SuspensedView>
            }
          />
        </Route>

        <Route
          path='service-cart/purchase-history'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PurchaseHistoryPage}
                moduleName="PurchaseHistory"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='office/data'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={OfficeDataPage}
                moduleName="Office Account Data"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='accounting/bill'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={BillingPage}
                moduleName="Billing"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='accounting/owner-bill'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ProductPaymentPage}
                moduleName="Invoice"
                userModules={userModule}
                skipDimOverlay={true}
              />
            </SuspensedView>
          }
        />

        {/* <Route
              path='accounting/bill/record-payment'
              element={
                <SuspensedView>
                  <ProtectedRoute 
                        component={RecordPaymentPage}
                        moduleName="" 
                        userModules={userModule}
                      />
              </SuspensedView>
              }
            /> */}
        <Route
          path='accounting/receipt'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ReceiptPage}
                moduleName="Receipts"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/deposit-refund'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={DepositRefundPage}
                moduleName="Deposit Refund"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/expenses'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ExpensesPage}
                moduleName="Expenses"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/payment-voucher'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PaymentVoucherPage}
                moduleName="Payment Voucher"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/e-invoice/setup'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={EInvoiceSetUpPage}
                moduleName="E-Invoice Form Set Up"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/creditNote'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={CreditNotePage}
                moduleName="Credit Note"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='accounting/debitNote'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={DebitNotePage}
                moduleName="Debit Note"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='inventory/list'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={InventoryListPage}
                moduleName="Inventory List"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route path='inventory/transaction'>
          <Route
            path=''
            element={
              <SuspensedView>
                <ProtectedRoute
                  component={InventoryTransactionPage}
                  moduleName="Inventory Transaction"
                  userModules={userModule}
                  skipDimOverlay={false}
                />
              </SuspensedView>
            } />
          <Route
            path=':itemId'
            element={
              <SuspensedView>
                <ProtectedRoute
                  component={InventoryTransactionPage}
                  moduleName="Inventory Transaction"
                  userModules={userModule}
                  skipDimOverlay={false}
                />
              </SuspensedView>
            } />
        </Route>

        <Route
          path='report/payout-report'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PropertyPayoutReportPage}
                moduleName="Management Property Payout Report"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='report/sale-report'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SaleReportPage}
                moduleName="Sale Report"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='smart-meter'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SmartMeterPage}
                moduleName="Smart Meter"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='smart-door-locks'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SmartLockPage}
                moduleName="Smart Door Locks"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='smart-door-locks/details/:id/:type'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SmartLockDetailsPage}
                moduleName="Smart Lock Details"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='gateway'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SmartLockGatewayPage}
                moduleName="Smart Lock Gateway"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='transaction/payment'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PaymentTransactionPage}
                moduleName="Payment Transaction"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='transaction/payout'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PayoutTransactionPage}
                moduleName="Payout Transaction"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='setup/billType'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={BillTypePage}
                moduleName="Bill Type"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='setup/contractorData'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ContractorDataPage}
                moduleName="Contractor Data"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='setup/expensesType'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ExpensesTypePage}
                moduleName="Expenses Type"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='setup/propertySite'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={PropertySitePage}
                moduleName="Property Site"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='setup/settings'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={SettingPage}
                moduleName="Settings"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />
        <Route
          path='setup/announcement'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={AnnouncementPage}
                moduleName="Announcement Management"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        <Route
          path='listing/nextsix-listing'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={NextsixListingPage}
                moduleName="Nextsix Listing"
                userModules={userModule}
                skipDimOverlay={false}
              />
            </SuspensedView>
          }
        />

        {/* <Route
          path='listing/showcase-listing'
          element={
            <SuspensedView>
              <ProtectedRoute
                component={ShowcaseListingPage}
                moduleName="Showcase Listing"
                userModules={userModule}
              />
            </SuspensedView>
          }
        /> */}
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
